import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideCalendar, DateAdapter, CalendarDateFormatter, CalendarMomentDateFormatter, MOMENT } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { registerLocaleData } from '@angular/common';
import localeHe from '@angular/common/locales/he';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { UtilService } from './services/util.service';
import { AuthService } from './services/auth.service';
import { TranslatorService } from './services/translator_service';
import { ResponsiveService } from './services/responsive.service';
import { PagerService } from './services/pager.service';
import { GoogleMapsLoaderService } from './services/google-maps-loader.service';
import { NgCircleProgressModule } from 'ng-circle-progress';

registerLocaleData(localeHe);

export function momentAdapterFactory() {
	return adapterFactory(moment);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    /* BLOCK bootstrap until the active language's translation file is in memory.
       The `| translate` pipe resolves against whatever ngx-translate holds at
       render time, so without this the very first paint after a hard refresh
       shows raw keys ("TITLES.CLUSTERS", "OTHER.SEARCH", ...) for a frame until
       the JSON arrives. Setting the language here also means `translate.currentLang()`
       is already correct when the rtlDiv directive constructs, so the layout paints
       in the right direction from the start instead of flipping.
       Escape hatches, so a slow/unreachable i18n file can never block the app:
       - .catch(): a failed fetch resolves immediately (keys show, app runs);
       - Promise.race with a 5s ceiling for a stalled download. */
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      // Same default as TranslatorService.initFromStoredLanguageOrDefault(), which
      // still runs in App.ngOnInit (it also re-inits when login remounts).
      let language = localStorage.getItem('language');
      if (language == null || language === '') {
        language = 'en';
        localStorage.setItem('language', language);
      }
      translate.setFallbackLang(language);
      return Promise.race([
        firstValueFrom(translate.use(language)).catch(() => undefined),
        new Promise<void>((resolve) => setTimeout(resolve, 5000))
      ]);
    }),
    // Kick off the Google Maps JS API load once at startup (non-blocking: the
    // promise is intentionally not returned, so bootstrap never waits on it).
    // Components that render <google-map> without awaiting the loader (the
    // site/ticket modals, bin-location, etc.) rely on the script already being
    // present; without this, opening one of them before a loader-aware page
    // (e.g. map-live-view) throws "Namespace google not found".
    provideAppInitializer(() => {
      const loader = inject(GoogleMapsLoaderService);
      /* BLOCK bootstrap until the Google Maps API is available, so every page -
         including a hard refresh directly on a maps page (/ticketManagement,
         /workplans, /binLiveInfoPage, install flows...) - renders with the
         'google' namespace already present. Two escape hatches guarantee the
         app always boots even when Google is unreachable:
         - .catch(): the script's onerror (offline/blocked network) resolves
           immediately and the app continues without maps;
         - Promise.race with a 10s ceiling: a pathologically stalled download
           can never hold the app hostage. */
      return Promise.race([
        loader.loadGoogleMaps().catch(() => { /* map pages degrade, app still boots */ }),
        new Promise<void>((resolve) => setTimeout(resolve, 10000))
      ]);
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // provideClientHydration(),
    provideHttpClient(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: `.json?v=${environment.appVersion}`,
      }),
    }),
    UtilService,
    AuthService,
    TranslatorService,
    ResponsiveService,
    PagerService,
    NgCircleProgressModule.forRoot({
      radius: 100,
      outerStrokeWidth: 16,
      innerStrokeWidth: 8,
      outerStrokeColor: "#78C000",
      innerStrokeColor: "#C7E596",
      animationDuration: 300
    }).providers!,
    provideLottieOptions({
      player: () => player,
    }),
    ...provideCalendar(
      { provide: DateAdapter, useFactory: momentAdapterFactory },
      {
        dateFormatter: {
          provide: CalendarDateFormatter,
          useClass: CalendarMomentDateFormatter,
        },
      }
    ),
    { provide: MOMENT, useValue: moment },
  ]
};
